import { AlertTriangle, CheckCircle2, ChevronRight, Clock, FileText, Heart, School, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { BookmarkButton } from '../../components/BookmarkButton';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { AdmissionDeclineModal } from './components/AdmissionDeclineModal';

interface Application {
  id: string;
  status: 'draft' | 'submitted' | 'exam_wait' | 'result_wait' | 'procedure_wait' | 'accepted' | 'enrolled' | 'rejected';
  universityName?: string;
  examName?: string;
  examDate?: string;
  resultDate?: string;
  procedureDeadline?: string;
  submittedAt?: string;
  completedAt?: string;
  offering?: {
    name: string;
  };
  updatedAt?: string;
  tenantId?: string;
}

interface Bookmark {
  id: string;
  targetType: 'tenant' | 'exam';
  targetId: string;
  tenantId?: string;
  createdAt: string;
  tenant?: {
    name: string;
    code: string;
    logoUrl?: string | null;
  };
}

export const DashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<Application[]>([]);
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('Student');

  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [selectedDeclineApp, setSelectedDeclineApp] = useState<Application | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const userStr = localStorage.getItem('currentUser');
        if (userStr) {
          const user = JSON.parse(userStr);
          setUserName(user.name);
        }

        if (!token) {
          navigate('/login');
          return;
        }

        const apiBase = import.meta.env.VITE_API_BASE || 'http://localhost:8787';

        // Fetch Applications
        const appRes = await fetch(`${apiBase}/api/applications/mine`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (appRes.ok) {
          const data = await appRes.json();
          setApplications(data);
        } else if (appRes.status === 401) {
          navigate('/login');
          return;
        }

        // Fetch Bookmarks
        const bookmarkRes = await fetch(`${apiBase}/api/bookmarks`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (bookmarkRes.ok) {
          const data = await bookmarkRes.json();
          setBookmarks(data.data || []);
        }
      } catch (e) {
        console.error('Failed to fetch dashboard data', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const handleDeclineClick = (app: Application) => {
    setSelectedDeclineApp(app);
    setDeclineModalOpen(true);
  };

  const handleDeclineConfirm = async (password: string) => {
    if (!selectedDeclineApp) return;

    try {
      const apiBase = import.meta.env.VITE_API_BASE || 'http://localhost:8787';
      const token = localStorage.getItem('accessToken');

      const res = await fetch(`${apiBase}/api/applications/${selectedDeclineApp.id}/decline`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-Id': selectedDeclineApp.tenantId || '',
        },
        body: JSON.stringify({ password }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to decline');
      }

      setApplications((apps) => apps.filter((a) => a.id !== selectedDeclineApp.id));
      setDeclineModalOpen(false);
      alert('辞退手続きが完了しました。\nAdmission Declined Successfully.');
    } catch (e: unknown) {
      console.error(e);
      if (e instanceof Error) {
        alert(`Error: ${e.message}`);
      } else {
        alert('An unexpected error occurred');
      }
    }
  };

  const handleBookmarkToggle = (id: string, isBookmarked: boolean) => {
    if (!isBookmarked) {
      setBookmarks((prev) => prev.filter((b) => b.targetId !== id));
    }
  };

  if (loading) {
    return (
      <div className="flex h-[50vh] w-full items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
          <p className="text-sm font-medium text-gray-500">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Status Groups
  const examWait = applications.filter((a) => a.status === 'exam_wait' || a.status === 'submitted'); // Assuming submitted leads to exam wait roughly
  const resultWait = applications.filter((a) => a.status === 'result_wait');
  const procedureWait = applications.filter((a) => a.status === 'accepted' || a.status === 'procedure_wait');
  const enrolled = applications.filter((a) => a.status === 'enrolled');
  const drafts = applications.filter((a) => a.status === 'draft');

  // Mock Preparation Status (In real app, fetch from API)
  // TODO: This should be real data from user profile
  const preparationStatus = {
    profile: true, // Mock: Profile done
    highSchool: false, // Mock: Needs update
    externalApi: false, // Mock: Not linked
  };

  // Calculate progress
  const prepCompletedCount = Object.values(preparationStatus).filter(Boolean).length;
  const prepTotalCount = 3;
  const isPrepComplete = prepCompletedCount === prepTotalCount;

  return (
    <div className="space-y-10 pb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Hero / Welcome Section */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-900 via-blue-900 to-indigo-950 px-8 py-12 text-white shadow-xl ring-1 ring-white/10">
        <div className="relative z-10 w-full">
          <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
                {t('dashboard.welcome', { name: userName })}
              </h1>
              <p className="mt-4 max-w-xl text-lg text-blue-100/80">{t('dashboard.welcomeMessage')}</p>
            </div>
            {/* Primary Action: Search Schools */}
            <Link to="/application/new">
              <Button
                size="lg"
                className="bg-white text-blue-900 hover:bg-blue-50 font-bold shadow-lg transition-all hover:scale-105 active:scale-95"
              >
                <Search className="mr-2 h-5 w-5" /> {t('dashboard.findUniversity')}
              </Button>
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-4 md:mt-16">
            <div className="rounded-xl bg-white/10 p-4 backdrop-blur-sm transition-colors hover:bg-white/20">
              <p className="text-sm font-medium text-blue-200">{t('dashboard.stats.total')}</p>
              <p className="mt-2 text-3xl font-bold">{applications.length}</p>
            </div>
            <div className="rounded-xl bg-white/10 p-4 backdrop-blur-sm transition-colors hover:bg-white/20">
              <p className="text-sm font-medium text-blue-200">{t('dashboard.sections.examWait')}</p>
              <p className="mt-2 text-3xl font-bold">{examWait.length}</p>
            </div>
            <div className="rounded-xl bg-white/10 p-4 backdrop-blur-sm transition-colors hover:bg-white/20">
              <p className="text-sm font-medium text-blue-200">{t('dashboard.sections.resultWait')}</p>
              <p className="mt-2 text-3xl font-bold">{resultWait.length}</p>
            </div>
            <div className="rounded-xl bg-white/10 p-4 backdrop-blur-sm transition-colors hover:bg-white/20">
              <p className="text-sm font-medium text-blue-200">{t('dashboard.sections.procedureWait')}</p>
              <p className="mt-2 text-3xl font-bold text-orange-300">{procedureWait.length}</p>
            </div>
          </div>
        </div>

        {/* Decorative background elements */}
        <div className="absolute -right-20 -top-20 h-96 w-96 rounded-full bg-blue-500/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-indigo-500/20 blur-3xl" />
      </section>

      {/* Preparation Status Section (New) */}
      <section className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <CheckCircle2 className={`w-5 h-5 ${isPrepComplete ? 'text-green-500' : 'text-gray-400'}`} />
              {t('dashboard.preparation.title')}
            </h3>
            <p className="text-sm text-gray-500 mt-1">{t('dashboard.preparation.completePrompt')}</p>
          </div>
          <div className="text-right">
            <span className="text-2xl font-bold text-gray-900">{prepCompletedCount}</span>
            <span className="text-gray-400">/{prepTotalCount}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Profile Status */}
          <div
            className={`flex items-center justify-between p-4 rounded-lg border ${preparationStatus.profile ? 'bg-green-50/50 border-green-200' : 'bg-white border-amber-200 border-l-4 border-l-amber-400'}`}
          >
            <div className="flex items-center gap-3">
              {preparationStatus.profile ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              )}
              <span className={`font-medium ${preparationStatus.profile ? 'text-gray-700' : 'text-gray-900'}`}>
                {t('dashboard.preparation.profile')}
              </span>
            </div>
            <Button variant="ghost" size="sm" asChild className="h-8">
              <Link to="/profile">{t('common.edit')}</Link>
            </Button>
          </div>

          {/* High School Status */}
          <div
            className={`flex items-center justify-between p-4 rounded-lg border ${preparationStatus.highSchool ? 'bg-green-50/50 border-green-200' : 'bg-white border-amber-200 border-l-4 border-l-amber-400'}`}
          >
            <div className="flex items-center gap-3">
              {preparationStatus.highSchool ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              )}
              <span className={`font-medium ${preparationStatus.highSchool ? 'text-gray-700' : 'text-gray-900'}`}>
                {t('dashboard.preparation.highSchool')}
              </span>
            </div>
            <Button variant="ghost" size="sm" asChild className="h-8">
              <Link to="/profile/highschool">{t('common.edit')}</Link>
            </Button>
          </div>

          {/* External API Status */}
          <div
            className={`flex items-center justify-between p-4 rounded-lg border ${preparationStatus.externalApi ? 'bg-green-50/50 border-green-200' : 'bg-white border-amber-200 border-l-4 border-l-amber-400'}`}
          >
            <div className="flex items-center gap-3">
              {preparationStatus.externalApi ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              )}
              <span className={`font-medium ${preparationStatus.externalApi ? 'text-gray-700' : 'text-gray-900'}`}>
                {t('dashboard.preparation.externalApi')}
              </span>
            </div>
            <Button variant="ghost" size="sm" asChild className="h-8">
              <Link to="/profile/api">{t('common.edit')}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Procedure Wait (Action Required) */}
      {procedureWait.length > 0 && (
        <section>
          <div className="mb-6 flex items-center gap-3">
            <div className="rounded-full bg-amber-100 p-2 text-amber-600">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
              {t('dashboard.sections.procedureWait')} / {t('dashboard.sections.actionRequired')}
            </h2>
          </div>
          <div className="grid gap-6">
            {procedureWait.map((app) => (
              <Card
                key={app.id}
                className="overflow-hidden border-none bg-amber-50/50 shadow-md ring-1 ring-amber-200 transaction-all hover:bg-amber-50"
              >
                <CardContent className="p-6 sm:p-8">
                  <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                    <div>
                      <Badge className="mb-3 bg-amber-500 px-3 py-1 text-sm font-bold text-white shadow-sm border-none">
                        {t('dashboard.cardStatus.passed')}
                      </Badge>
                      <h3 className="text-2xl font-bold text-gray-900">{app.offering?.name || 'Application'}</h3>
                      <div className="mt-2 flex items-center gap-2 text-amber-800">
                        <Clock className="h-4 w-4" />
                        <span className="font-semibold">
                          {t('dashboard.cardMessages.deadline', {
                            date: app.procedureDeadline || '2026-03-31',
                          })}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-amber-700/80">{t('dashboard.cardMessages.completePrompt')}</p>
                    </div>
                    <div>
                      <Link to={`/enrollment?id=${app.id}`}>
                        <Button
                          size="lg"
                          className="w-full bg-gradient-to-r from-amber-500 to-orange-600 font-bold text-white shadow-lg hover:from-amber-600 hover:to-orange-700 md:w-auto"
                        >
                          {t('dashboard.actions.startProcedure')}
                          <ChevronRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Exam Wait / Result Wait */}
      {(examWait.length > 0 || resultWait.length > 0) && (
        <section>
          <div className="mb-6 flex items-center gap-3">
            <div className="rounded-full bg-blue-100 p-2 text-blue-600">
              <FileText className="h-6 w-6" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">{t('dashboard.status')}</h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {/* Exam Wait */}
            {examWait.map((app) => (
              <Card
                key={app.id}
                className="group cursor-pointer border-none shadow-sm ring-1 ring-gray-200 transition-all hover:shadow-lg"
              >
                <CardHeader className="bg-gray-50/50 pb-4">
                  <div className="flex justify-between items-start">
                    <Badge variant="secondary" className="bg-white shadow-sm text-blue-700">
                      {t('dashboard.cardStatus.examWait')}
                    </Badge>
                    <span className="text-xs font-medium text-gray-500">{app.updatedAt?.split('T')[0]}</span>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{app.offering?.name || 'Application'}</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {t('dashboard.cardMessages.examDate', {
                      date: app.examDate || 'TBD',
                    })}
                  </p>
                  <Button variant="outline" size="sm" className="w-full">
                    {t('dashboard.actions.downloadTicket')} / {t('dashboard.actions.printTicket')}
                  </Button>
                  <div className="mt-2 space-y-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-xs text-gray-600 justify-start h-auto py-1"
                    >
                      <FileText className="mr-2 h-3 w-3" /> {t('dashboard.actions.digitalSurvey')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-xs text-gray-600 justify-start h-auto py-1"
                    >
                      <Clock className="mr-2 h-3 w-3" /> {t('dashboard.actions.mailSurvey')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {/* Result Wait */}
            {resultWait.map((app) => (
              <Card
                key={app.id}
                className="group cursor-pointer border-none shadow-sm ring-1 ring-gray-200 transition-all hover:shadow-lg"
              >
                <CardHeader className="bg-gray-50/50 pb-4">
                  <div className="flex justify-between items-start">
                    <Badge variant="secondary" className="bg-white shadow-sm text-purple-700">
                      {t('dashboard.cardStatus.resultWait')}
                    </Badge>
                    <span className="text-xs font-medium text-gray-500">{app.updatedAt?.split('T')[0]}</span>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{app.offering?.name || 'Application'}</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {t('dashboard.cardMessages.resultDate', {
                      date: app.resultDate || 'TBD',
                    })}
                  </p>
                  <Button variant="secondary" size="sm" className="w-full" disabled>
                    {t('dashboard.actions.viewResult')} (Locked)
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Bookmarked Universities */}
      {bookmarks.length > 0 && (
        <section>
          <div className="mb-6 flex items-center gap-3">
            <div className="rounded-full bg-pink-100 p-2 text-pink-600">
              <Heart className="h-6 w-6" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">{t('dashboard.sections.bookmarks')}</h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {bookmarks.map((bm) => (
              <Card
                key={bm.id}
                className="group relative cursor-pointer border-none shadow-sm ring-1 ring-gray-200 transition-all hover:-translate-y-1 hover:shadow-lg"
              >
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <Badge variant="secondary" className="bg-gray-100">
                      {bm.tenant?.code || 'University'}
                    </Badge>
                    <BookmarkButton
                      targetType={bm.targetType}
                      targetId={bm.targetId}
                      initialIsBookmarked={true}
                      bookmarkId={bm.id}
                      onToggle={(val) => handleBookmarkToggle(bm.targetId, val)}
                    />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{bm.tenant?.name || 'Unknown University'}</h3>
                  <p className="text-xs text-gray-500 mb-6">Added on {bm.createdAt?.split('T')[0]}</p>

                  <Link to={`/application/new?tenantId=${bm.targetId}`}>
                    <Button
                      size="sm"
                      className="w-full bg-blue-50 text-blue-600 hover:bg-blue-100 border-none shadow-none"
                    >
                      {t('dashboard.actions.apply')} / {t('dashboard.actions.requestMaterials')}{' '}
                      <ChevronRight className="ml-1 h-3 w-3" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Enrollment Section */}
      {enrolled.length > 0 && (
        <section>
          <div className="mb-6 flex items-center gap-3">
            <div className="rounded-full bg-emerald-100 p-2 text-emerald-600">
              <School className="h-6 w-6" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">{t('dashboard.sections.enrolled')}</h2>
          </div>
          <div className="grid gap-6">
            {enrolled.map((app) => (
              <Card
                key={app.id}
                className="group overflow-hidden border-none shadow-md ring-1 ring-black/5 transition-all hover:shadow-lg"
              >
                <div className="absolute left-0 top-0 h-full w-1.5 bg-emerald-500" />
                <CardContent className="p-6 sm:p-8">
                  <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                    <div className="space-y-4">
                      <div className="flex flex-wrap gap-2">
                        <Badge className="bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-800 hover:bg-emerald-200 border-none">
                          <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                          {t('dashboard.cardStatus.completed')}
                        </Badge>
                        <Badge variant="outline" className="text-gray-500">
                          ID: {app.id}
                        </Badge>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-1">
                          {app.offering?.name || 'University Application'}
                        </h3>
                        <p className="text-gray-500">{t('dashboard.cardMessages.congrats')}</p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <Button
                        variant="outline"
                        className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => handleDeclineClick(app)}
                      >
                        {t('dashboard.actions.decline')}
                      </Button>
                      <Button className="bg-emerald-600 text-white shadow-sm hover:bg-emerald-700">
                        {t('dashboard.actions.viewDocs')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Draft Cards - Kept at bottom if any */}
      {drafts.length > 0 && (
        <section>
          <div className="mb-6 flex items-center gap-3">
            <div className="rounded-full bg-gray-100 p-2 text-gray-600">
              <FileText className="h-6 w-6" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">{t('dashboard.sections.active')}</h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {drafts.map((app) => (
              <Card
                key={app.id}
                className="group relative cursor-pointer overflow-hidden border-2 border-dashed border-gray-200 shadow-none transition-all hover:border-blue-300 hover:bg-blue-50/30"
              >
                <Link to={`/application/${app.id}/edit`} className="absolute inset-0 z-10" />
                <CardContent className="flex h-full flex-col justify-between p-6">
                  <div>
                    <Badge variant="outline" className="mb-4 text-gray-500">
                      {t('dashboard.cardStatus.draft')}
                    </Badge>
                    <h3 className="text-lg font-bold text-gray-900">{app.offering?.name || 'New Application'}</h3>
                    <p className="mt-2 text-sm text-gray-500">Last edited: {app.updatedAt?.split('T')[0]}</p>
                  </div>
                  <div className="mt-6 flex items-center text-sm font-bold text-blue-600 opacity-0 transition-opacity group-hover:opacity-100">
                    Continue Editing <ChevronRight className="ml-1 h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {selectedDeclineApp && (
        <AdmissionDeclineModal
          open={declineModalOpen}
          universityName={selectedDeclineApp.offering?.name || 'This University'}
          onClose={() => setDeclineModalOpen(false)}
          onConfirm={handleDeclineConfirm}
        />
      )}
    </div>
  );
};
